/**
 * AdditionalVaultsPanel — renders the 4 non-pilot vaults
 * (CREDIT_FACILITY_CUSTODY, LOAN_OBLIGATION, RESERVE_CAPITAL,
 * STRATEGIC_CAPITAL) in a clearly-labelled secondary strip beneath
 * the main 6-vault dashboard.
 *
 * The four vaults are shown with their balance (when present) but
 * marked with a "Under Development" or "Experimental" badge so the
 * user understands they are visible but not yet pilot-ready.
 *
 * This component does NOT change the existing six-vault layout.
 * It blends as a secondary card beneath the primary vault grid.
 */
import { useVaultRegistry } from "@/hooks/useVaultRegistry";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, FlaskConical } from "lucide-react";

function MaturityBadge({ maturity }) {
  if (maturity === "under_development") {
    return (
      <Badge
        variant="outline"
        className="text-[9px] border-amber-500/30 text-amber-300/90 bg-amber-500/[0.05]"
        data-testid="badge-under-development"
      >
        <ShieldAlert className="w-2.5 h-2.5 mr-1" /> Under Development
      </Badge>
    );
  }
  if (maturity === "experimental") {
    return (
      <Badge
        variant="outline"
        className="text-[9px] border-violet-500/30 text-violet-300/90 bg-violet-500/[0.05]"
        data-testid="badge-experimental"
      >
        <FlaskConical className="w-2.5 h-2.5 mr-1" /> Experimental
      </Badge>
    );
  }
  return null;
}

export default function AdditionalVaultsPanel({ balancesByKey = {}, formatMoney = (n) => `£${n}` }) {
  const { underDevelopmentVaults, loading, error } = useVaultRegistry();
  if (loading || error) return null;
  if (!underDevelopmentVaults.length) return null;

  return (
    <Card
      className="bg-slate-900/30 border-slate-800/40"
      data-testid="additional-vaults-panel"
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div>
            <h3 className="text-sm font-barlow font-bold text-slate-200" data-testid="additional-vaults-title">
              Additional Vaults In Development
            </h3>
            <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
              Six vaults are pilot-ready. The four below are visible as part of the
              full Sentient Vault ten-vault architecture and remain under controlled
              development — assign manually until automatic allocation is enabled.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {underDevelopmentVaults.map((v) => {
            const balance = balancesByKey[v.key] ?? 0;
            return (
              <div
                key={v.key}
                className="rounded-lg border border-slate-700/40 bg-slate-900/40 p-3"
                data-testid={`additional-vault-${v.key.toLowerCase()}`}
              >
                <div className="flex items-start justify-between gap-1 mb-2">
                  <span className="text-[10px] text-slate-400 font-mono uppercase tracking-wider truncate">
                    {v.label}
                  </span>
                  <MaturityBadge maturity={v.maturity} />
                </div>
                <div className="font-mono font-bold text-base text-slate-100"
                     data-testid={`additional-vault-balance-${v.key.toLowerCase()}`}>
                  {formatMoney(balance)}
                </div>
                <p className="text-[10px] text-slate-500 mt-2 leading-snug line-clamp-2">
                  {v.user_explanation}
                </p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
